/**
 * Создание рассылки: имя + сегмент аудитории + rich-text сообщение.
 * При клике «Создать и запустить» — POST draft → compile → start.
 */
import { useEffect, useState } from "react";
import {
  Card, Input, Radio, Space, Button, Select, Typography, message, Alert,
} from "antd";
import { SendOutlined, SaveOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router";
import { API_URL } from "../../dataProvider";
import { RichTextEditor } from "../../components/RichTextEditor";

interface TagRow { id: number; name: string; users_count: number; }

type SegmentType = "all" | "tag";

export const BroadcastCreate = () => {
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [segmentType, setSegmentType] = useState<SegmentType>("all");
  const [tagId, setTagId] = useState<number | null>(null);
  const [html, setHtml] = useState("");
  const [isEmpty, setIsEmpty] = useState(true);
  const [tags, setTags] = useState<TagRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [previewCount, setPreviewCount] = useState<number | null>(null);

  useEffect(() => {
    fetch(`${API_URL}/admin/tags`, { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then(setTags);
  }, []);

  const segmentFilter = (): Record<string, unknown> => {
    if (segmentType === "all") return { type: "all" };
    if (segmentType === "tag" && tagId) return { type: "tag", tag_id: tagId };
    return { type: "all" };
  };

  const create = async (andStart: boolean) => {
    if (!name.trim()) { message.warning("Введите название"); return; }
    if (isEmpty) { message.warning("Текст сообщения пустой"); return; }
    if (segmentType === "tag" && !tagId) {
      message.warning("Выберите метку"); return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/admin/broadcasts`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          segment_filter: segmentFilter(),
          content_blocks: [{ type: "html", html: html.trim() }],
        }),
      });
      if (!r.ok) {
        message.error(`Ошибка создания: ${(await r.text()).slice(0, 200)}`);
        return;
      }
      const { id } = await r.json();

      if (andStart) {
        const c = await fetch(`${API_URL}/admin/broadcasts/${id}/compile`,
          { method: "POST", credentials: "include" });
        if (!c.ok) {
          message.error(`Compile ошибка: ${(await c.text()).slice(0, 200)}`);
          nav(`/broadcasts/${id}`); return;
        }
        const s = await fetch(`${API_URL}/admin/broadcasts/${id}/start`,
          { method: "POST", credentials: "include" });
        if (!s.ok) {
          message.error(`Start ошибка: ${(await s.text()).slice(0, 200)}`);
          nav(`/broadcasts/${id}`); return;
        }
        message.success("Рассылка запущена. Прогресс — на странице списка.");
      } else {
        message.success("Сохранено как черновик");
      }
      nav("/broadcasts");
    } catch (e) {
      message.error(`Сетевая ошибка: ${(e as Error).message}`);
    } finally { setBusy(false); }
  };

  return (
    <Card title="Новая рассылка">
      <Space direction="vertical" size="large" style={{ width: "100%" }}>

        <div>
          <Typography.Text strong>Название (внутреннее)</Typography.Text>
          <Input value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Например: 'Промо июль 2026'" maxLength={200} />
        </div>

        <div>
          <Typography.Text strong>Кому отправить</Typography.Text>
          <Radio.Group value={segmentType} onChange={(e) => setSegmentType(e.target.value)}
                       style={{ display: "block", marginTop: 8 }}>
            <Space direction="vertical">
              <Radio value="all">Все юзеры (кроме заблокировавших бот)</Radio>
              <Radio value="tag">
                <Space>
                  По метке:
                  <Select style={{ width: 240 }} placeholder="Выберите метку"
                    value={tagId ?? undefined}
                    onChange={(v) => { setTagId(v); setSegmentType("tag"); }}
                    options={tags.map(t => ({
                      value: t.id,
                      label: `${t.name} (${t.users_count})`,
                    }))}
                    disabled={segmentType !== "tag"}
                  />
                </Space>
              </Radio>
            </Space>
          </Radio.Group>
          <div style={{ marginTop: 8, padding: "6px 12px",
                        background: "#f0f5ff", borderRadius: 4,
                        display: "inline-block" }}>
            <Typography.Text style={{ fontSize: 13 }}>
              📊 Получат рассылку:{" "}
              <strong style={{ color: "#1677ff" }}>
                {previewCount === null ? "—" : previewCount}
              </strong>{" "}
              реальных юзеров
              <Typography.Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                (только с валидным TG ID и не заблокировавшие бот)
              </Typography.Text>
            </Typography.Text>
          </div>
        </div>

        <div>
          <Typography.Text strong>Текст сообщения</Typography.Text>
          <div style={{ marginTop: 8 }}>
            <RichTextEditor
              placeholder="Привет! Сегодня мы запускаем…"
              onChange={(h, empty) => { setHtml(h); setIsEmpty(empty); }}
              minHeight={140}
            />
          </div>
          <Alert type="info" showIcon style={{ marginTop: 8 }}
            message="Telegram поддерживает <b>жирный</b>, <i>курсив</i>, <s>зачёркнутый</s>, <code>моноширинный</code> и ссылки."
          />
        </div>

        <Space>
          <Button icon={<SaveOutlined />} loading={busy}
                  onClick={() => create(false)}>
            Сохранить черновик
          </Button>
          <Button type="primary" icon={<SendOutlined />} loading={busy}
                  onClick={() => create(true)}>
            Создать и запустить
          </Button>
        </Space>

      </Space>
    </Card>
  );
};
